import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SmsTemplateService } from '../../pages/definition/sms-template/services/sms-template.service';
import { EmailTemplateService } from '../../pages/definition/email-template/services/email-template.service';

export type SendType = 'email' | 'sms';

export interface SendActionPayload {
  type: SendType;
  caseId?: number;
  receivers: string[];
  templateId?: any;
  subject?: string;
  body?: string;
}

@Component({
  selector: 'app-send-mail-sms',
  templateUrl: './send-mail-sms.component.html',
  styleUrls: ['./send-mail-sms.component.scss']
})
export class SendMailSmsComponent {

  @Input() type: SendType = 'email';
  @Input() contacts?: any[];
  @Input() distanced?: boolean = false;

  @Output() sendAction = new EventEmitter<SendActionPayload>();
  @Output() closed = new EventEmitter<void>();

  popup = false;
  caseId?: number;

  /** SELECTED */
  selectedValues: string[] = [];

  /** DEFAULT VALUES */
  defaultValues: string[] = [];
  defaultLabelMap: Record<string, string> = {};

  /** TEMPLATE */
  emailTemplates: any[] = [];
  smsTemplates: any[] = [];
  selectedTemplate: any = null;

  templatePreview?: {
    title?: string;
    content?: string;
  } = {
      title: '',
      content: ''
    };

  templatesLoading = false;
  constructor(
    private readonly smsTemplateService: SmsTemplateService,
    private readonly emailTemplateService: EmailTemplateService
  ) { }

  open(caseId?: number, defaultValues?: any[] | null, type?: SendType) {
    if (type) this.type = type;
    this.caseId = caseId;

    this.selectedValues = [];
    this.defaultValues = [];
    this.defaultLabelMap = {};
    this.selectedTemplate = null;
    this.templatePreview = { title: '', content: '' };

    this.emailTemplates = [];
    this.smsTemplates = [];
    this.templatesLoading = false;
     const collected: string[] = [];

    if (Array.isArray(defaultValues)) {
      defaultValues.forEach(item => {
        if (this.type === 'email' && item?.email) {
          const email = this.normalize(item.email);
          collected.push(email);
          this.defaultLabelMap[email] = 'Alıcı Email';
        }

        if (this.type === 'sms' && item?.phone) {
          const phone = this.normalize(item.phone);
          collected.push(phone);
          this.defaultLabelMap[phone] = 'Alıcı Telefon';
        }
      });
    }

    const uniqDefaults = this.uniq(collected);
    this.defaultValues = uniqDefaults;
    this.selectedValues = [...uniqDefaults]; 


    if (this.distanced) this.loadTemplates();
    this.popup = true;
  }

  loadTemplates() {
    this.templatesLoading = true;

    if (this.type === 'sms') {
      this.smsTemplateService.getAllFilter({}, {
        'filter.isActive': true,
        'filter.getAll': true,
        'filter.distancedSms': true
      }).subscribe({
        next: (res) => {
          this.smsTemplates = (res?.items ?? []).map((template: any) => ({
            ...template,
            toPhoneNumbers: Array.isArray(template.toPhoneNumbers)
              ? template.toPhoneNumbers.map((p: string) => p?.replaceAll(/\s+/g, ''))
              : []
          }));
          this.templatesLoading = false;
        },
        error: () => {
          this.templatesLoading = false;
        }
      });
    }

    if (this.type === 'email') {
      this.emailTemplateService.getAllFilter({}, {
        'filter.isActive': true,
        'filter.getAll': true,
        'filter.distancedEmail': true
      }).subscribe({
        next: (res) => {
          this.emailTemplates = res?.items ?? [];
          this.templatesLoading = false;
        },
        error: () => {
          this.templatesLoading = false;
        }
      });
    }
  }

  onTemplateChange(template: any) {
    this.selectedTemplate = template;

    if (this.type === 'email') {
      this.templatePreview = { title: template.emailTitle, content: template?.emailContent ?? template?.content };

      const merged = this.uniq([...(this.defaultValues ?? []), ...(template.toEmails ?? [])]);
      this.selectedValues = merged;
    }

    if (this.type === 'sms') {
      this.templatePreview = { title: template.templateName, content: template?.content };

      const merged = this.uniq([...(this.defaultValues ?? []), ...(template.toPhoneNumbers ?? [])]);
      this.selectedValues = merged;
    }
  }

  /* ---------------- CONTACT FILTER ---------------- */

  get selectableValues(): string[] {
    if (!Array.isArray(this.contacts)) return [];

    const raw = this.type === 'email'
      ? this.contacts.map(c => c?.email)
      : this.contacts.map(c => c?.phone);

    return this.uniq(raw.filter(Boolean));
  }


  /* ---------------- CHIP ACTIONS ---------------- */

  toggleValue(value: string) {
    this.selectedValues = this.selectedValues.includes(value)
      ? this.selectedValues.filter(v => v !== value)
      : [...this.selectedValues, value];
  }

  handleChipAdd(event: any) {
    const value = event.value?.trim().replaceAll(/\s+/g, '');
    if (!value) {
      this.selectedValues = this.selectedValues.filter(v => v !== event.value);
      return;
    }

    if (this.type === 'email') {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(value)) {
        this.selectedValues = this.selectedValues.filter(v => v !== value);
      }
    }

    if (this.type === 'sms') {
      const regex = /^[0-9+]{10,15}$/;
      if (!regex.test(value)) {
        this.selectedValues = this.selectedValues.filter(v => v !== value);
      }
    }
  }

  /* ---------------- SEND ---------------- */

  send() {
    if (!this.selectedValues.length) return;

    this.sendAction.emit({
      type: this.type,
      caseId: this.caseId,
      receivers: this.selectedValues,
      templateId: this.selectedTemplate?.id,
      subject: this.selectedTemplate?.templateName ?? undefined,
      body: this.selectedTemplate?.content ?? undefined
    });

    this.close();
  }

  
  close() {
    this.popup = false;
    this.caseId = undefined;
    this.selectedValues = [];
    this.defaultValues = [];
    this.defaultLabelMap = {};
    this.selectedTemplate = null;
    this.templatePreview = { title: '', content: '' };

    this.templatesLoading = false;
    this.emailTemplates = [];
    this.smsTemplates = [];

    this.closed.emit();
  }


  private normalize(v: string): string {
    return (v ?? '').trim().replaceAll(/\s+/g, '');
  }

  private uniq(values: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of values) {
      const n = this.normalize(v);
      if (!n) continue;
      if (!seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  }

}
